import { FileTextIcon, Trash2Icon } from 'lucide-react'

import { Button } from '@/components/ui'
import { memberPageLabel } from '@/modules/insights/utils'
import type { Transaction } from '@/modules/projects'
import { cn, formatDate, numberToCurrency } from '@/utils'

interface MemberRowProps {
  member: Transaction
  fileName: string
  isSelected: boolean
  onSelect: () => void
  onDelete: () => void
}

export const MemberRow = ({
  member,
  fileName,
  isSelected,
  onSelect,
  onDelete,
}: MemberRowProps) => {
  const pageLabel = memberPageLabel(member)

  return (
    <li
      className={cn(
        'flex items-center gap-2 rounded-lg border p-3 transition-colors',
        isSelected
          ? 'border-amber-300 bg-amber-50/70 ring-1 ring-amber-200'
          : 'hover:bg-blue-1',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        title="Show this one on the statement"
        className="min-w-0 flex-1 cursor-pointer space-y-1 text-left"
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-sm font-medium text-foreground">
            {member.description}
          </span>
          <span className="shrink-0 text-sm tabular-nums text-foreground">
            {numberToCurrency(member.amount)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <FileTextIcon className="size-3.5 shrink-0" />
          <span className="truncate">{fileName}</span>
          <span>·</span>
          <span className="tabular-nums">
            {pageLabel ?? 'page not recorded'}
          </span>
          <span>·</span>
          <span className="tabular-nums">
            {member.date ? formatDate(member.date) : '--'}
          </span>
        </div>
      </button>
      <Button
        theme="ghost"
        size="sm"
        isIcon
        className="cursor-pointer text-muted-foreground hover:text-destructive"
        onClick={onDelete}
      >
        <Trash2Icon className="size-4" />
        <span className="sr-only">Delete this transaction</span>
      </Button>
    </li>
  )
}
